// The load mix and the active round of a hull (R2017, R2031;
// ui-map-interface.md §8 "The round pick", §12 "FLEET · MISSIONS").
//
// A hull carries a count of each round kind (`FleetState.rounds`) and one
// active round (`FleetState.active_round`). The pens keep the load mix of
// the next deploy (`ShipStock.load_mix`, empty = AUTO). The server owns
// every rule; this module only reads the wire and the hello catalog:
//
//   - the fixed order Harpoon, Remora, Torpor is the catalog's kinds that
//     no base fires, in enum order (the server's `game.ShipRoundKinds`);
//   - the firing kind is the active kind while it holds a round, else the
//     next kind with a round in the fixed order, which wraps
//     (`game.Rounds.Firing`);
//   - a mix fits while the sum of count × cargo stays at or under the
//     magazine cargo (`game.MixFits`).
//
// The module is pure: it reads its arguments and writes no state.

import { card as S } from '../gen/strings';
import { type Choice, valueChoice, valueForm } from './choice/choice';
import { esc } from './esc';
import { magazineFor } from './format';
import { roundOrder, type RoundSpec } from './roundorder';

export { holdRoundOrder, roundOrder, type RoundSpec } from './roundorder';

/** One row of a load: a kind and its count. */
export interface Stock {
  kind: number;
  count: number;
}

/** The kinds a hull class can load, in the fixed order. */
export function loadableKinds(spec: { torpedoes: readonly number[] } | undefined,
  torpedoes: readonly RoundSpec[]): number[] {
  const can = new Set(spec?.torpedoes ?? []);
  return roundOrder(torpedoes).filter((k) => can.has(k));
}

/**
 * The catalog rows AUTO loads (arcade.md §4.6): the rounds an arcade hull
 * with no rounds mix can carry. AUTO never takes a row whose `auto` is
 * false (R2779). Such a round flies in a match only through the rounds mix
 * of a variant (R3215), and the round pick adds it from the load.
 */
export function arcadeRounds<T extends { auto: boolean }>(torpedoes: readonly T[]): T[] {
  return torpedoes.filter((t) => t.auto);
}

/** The count of one kind in a load, 0 when the load holds none. */
export function countOf(load: readonly Stock[], kind: number): number {
  let n = 0;
  for (const r of load) if (r.kind === kind) n += r.count;
  return n;
}

/** The rounds of a load, all kinds together. */
export function totalOf(load: readonly Stock[]): number {
  let n = 0;
  for (const r of load) n += r.count;
  return n;
}

/**
 * The kind the next shot fires (`game.Rounds.Firing`): the active kind
 * while it holds a round, else the next kind with a round in the fixed
 * order, which wraps. An empty load answers the active kind, so the
 * reload look and the aim ring still know the round the commander picked.
 */
export function firingKind(load: readonly Stock[], active: number, order: readonly number[]): number {
  if (active > 0 && countOf(load, active) > 0) return active;
  const at = order.indexOf(active);
  for (let i = 1; i <= order.length; i++) {
    const k = order[(at + i + order.length) % order.length];
    if (countOf(load, k) > 0) return k;
  }
  return active;
}

/** The cargo of one kind, 0 for a kind the catalog does not name. */
function cargoOf(torpedoes: readonly RoundSpec[], kind: number): number {
  return torpedoes.find((t) => t.id === kind)?.cargo ?? 0;
}

/**
 * The cargo of a list of rows: the count of each row × the cargo of one item
 * of its kind, which `cargoOf` answers. It is the one sum of the magazine
 * (`mixCargo`) and of a stow (`stowCargo` of the sapper card and of the
 * sapper work layer).
 */
export function cargoSum(
  rows: readonly { kind: number; count: number }[], cargoOf: (kind: number) => number,
): number {
  let n = 0;
  for (const r of rows) n += r.count * cargoOf(r.kind);
  return n;
}

/** The magazine room a load takes, in cargo units (numbers.md §5). */
export function mixCargo(load: readonly Stock[], torpedoes: readonly RoundSpec[]): number {
  return cargoSum(load, (k) => cargoOf(torpedoes, k));
}

/**
 * The magazine as a count of rounds: the cargo over the cargo of the
 * smallest round the hull loads. All three ship rounds take 5 today, so a
 * Lancer's 60 reads 12. 0 when the hull loads nothing.
 */
export function magazineRounds(magazineCargo: number, kinds: readonly number[],
  torpedoes: readonly RoundSpec[]): number {
  let least = 0;
  for (const k of kinds) {
    const c = cargoOf(torpedoes, k);
    if (c > 0 && (least === 0 || c < least)) least = c;
  }
  return least > 0 ? Math.floor(magazineCargo / least) : 0;
}

/** One more round of `kind` still fits the magazine (`game.MixFits`). */
export function canAdd(load: readonly Stock[], kind: number, magazineCargo: number,
  torpedoes: readonly RoundSpec[]): boolean {
  const c = cargoOf(torpedoes, kind);
  return c > 0 && mixCargo(load, torpedoes) + c <= magazineCargo;
}

/** A load with `kind` set to `count`, in the fixed order, with no empty row. */
export function withCount(load: readonly Stock[], kind: number, count: number,
  order: readonly number[]): Stock[] {
  const kinds = new Set([...order, ...load.map((r) => r.kind)]);
  const out: Stock[] = [];
  for (const k of [...kinds].sort((a, b) => a - b)) {
    const n = k === kind ? count : countOf(load, k);
    if (n > 0) out.push({ kind: k, count: n });
  }
  return out;
}

/**
 * The mix as words: `6 HARPOON · 3 REMORA` in full, `6H · 3R` short. Only
 * the kinds with a round stand, in the order of the load. An empty load
 * answers ''.
 */
export function mixText(load: readonly Stock[], nameOf: (kind: number) => string, short: boolean): string {
  const parts: string[] = [];
  for (const r of load) {
    if (r.count <= 0) continue;
    const name = nameOf(r.kind).toUpperCase();
    parts.push(short
      ? S.mix_short({ n: r.count, letter: name.charAt(0) })
      : S.mix_part({ n: r.count, name }));
  }
  return parts.join(S.mix_sep);
}

/** The kinds of a load that hold a round. */
export function kindsAboard(load: readonly Stock[]): number {
  return load.filter((r) => r.count > 0).length;
}

/** The mix in the form a line takes: the full form for one kind, the
 *  short form for two or more, so a narrow line holds it. */
export function mixLine(load: readonly Stock[], nameOf: (kind: number) => string): string {
  return mixText(load, nameOf, kindsAboard(load) > 1);
}

/**
 * The magazine of a hull as a count of rounds, for the TUBES vital and the
 * load part of a row (R836): the class cargo over the cargo of the kind
 * that fires, or of the class's first kind while nothing is aboard, so
 * empty tubes still show the magazine they would fill.
 */
export function hullMagazine(
  spec: { cargo: number; torpedoes?: readonly number[]; stows?: readonly number[] } | undefined,
  firing: number, torpedo: (kind: number) => { cargo: number } | undefined,
): number {
  // A match's ship table may leave a list out, so both reads are guarded.
  const k = firing || spec?.torpedoes?.[0] || spec?.stows?.[0] || 0;
  return magazineFor(spec, torpedo(k));
}

/**
 * The kinds of the round pick (R2031): every kind the hull can load and
 * every kind aboard, in the fixed order. The pick stands only for two or
 * more, so a hull of one kind has none.
 */
export function pickKinds(loadable: readonly number[], load: readonly Stock[],
  order: readonly number[]): number[] {
  const kinds = new Set([...loadable, ...load.filter((r) => r.count > 0).map((r) => r.kind)]);
  const known = order.filter((k) => kinds.has(k));
  return [...known, ...[...kinds].filter((k) => !order.includes(k)).sort((a, b) => a - b)];
}

/**
 * The kind the round key switches to (R2042): the kind after `chosen` in
 * the pick, which wraps. A `chosen` outside the pick answers the first
 * kind. 0 while the pick holds fewer than two kinds, because a hull of
 * one kind has no pick.
 */
export function nextRound(kinds: readonly number[], chosen: number): number {
  if (kinds.length < 2) return 0;
  return kinds[(kinds.indexOf(chosen) + 1) % kinds.length];
}

/**
 * The round pick of a fleet card (R2031) and of the own hold card (R2783):
 * a chooser of the choice kit in its compact size under the cap ROUND, one
 * option for each kind with the count aboard (or held). The option of
 * `chosen` is lit. An empty kind keeps its option and its press: the
 * server then fires the next kind with rounds. `idOf` writes the press id
 * of a kind, which the host's dispatcher reads.
 *
 * The form follows the count (§18.2): a pill group up to `choice_seg_max`
 * kinds, else an option list, else a select box. A Lancer loads eight
 * kinds since the torpedo refresh (R2779), and eight pills do not fit a
 * telephone card. The key cap stands on the pill the key switches to, or
 * beside the cap when the form has no pills.
 */
export function roundPickHtml(o: {
  kinds: readonly number[]; load: readonly Stock[]; chosen: number; act: string;
  nameOf: (kind: number) => string; idOf: (kind: number) => string;
  /** The cap of the round key: it shows on the pill the key switches to. */
  key?: string;
  /** The unique name of the select box, when the count gives one. */
  uid?: string;
}): string {
  const next = o.key ? nextRound(o.kinds, o.chosen) : 0;
  const pills = valueForm(o.kinds.length, false) === 'pills';
  const list: Choice[] = o.kinds.map((k) => ({
    id: o.idOf(k), label: o.nameOf(k).toUpperCase(), n: countOf(o.load, k), title: S.round_pick_title,
    key: pills && k === next ? o.key : undefined,
  }));
  const keyCap = !pills && next && o.key ? ` <kbd class="ck-key">${esc(o.key)}</kbd>` : '';
  return `<div class="ck-sect"><span class="ck-cap">${S.f_round}${keyCap}</span>`
    + valueChoice({
      list, chosen: o.idOf(o.chosen), act: o.act, label: S.f_round, size: 'compact',
      uid: o.uid ?? o.act, title: S.f_round,
    })
    + '</div>';
}
